import { Heading } from '@/components/Heading'
import { Box } from '@/components/Box'
import Input from '@/components/Input'
import { Select } from '@/components/Select'
import { Question } from '@/components/Question'
import { drivingLicenceOptions, listOfCountries, martialStatusOptions } from '@/data/selectOptions'
import { Checkbox } from '@/components/Checkbox'
import { Button } from '@/components/Button'
import AddressFinderInput from '@/components/AddressFinderInput'
import { ROUTES } from '@/config/routes'
import { Header } from '@/components/HeaderBusiness'
import useFormContext from '@/context/useFormContext'
import { useForm } from 'react-hook-form'
import { emailRegex, mobileNumberRegex } from '@/utils/constant'
import { subYears, format } from 'date-fns'
import { useEffect, useState } from 'react'
import { generateAgeOptions, generateMonthOptions, generateYearsOptions } from '@/utils/dateUtils'
import { Address } from 'react-addressfinder'
import { ISO_3166_MAPPER } from '@/data/ISO_3166_MAPPER'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { preparePayload } from '@/services/saveService'
import { scrollToFirstError } from '@/utils/helper'
import { normalizeCountryValue } from '@/utils/countryUtils'

const minDateOfBirth = format(subYears(new Date(), 100), 'yyyy-MM-dd')
const maxDateOfBirth = format(subYears(new Date(), 18), 'yyyy-MM-dd')
const maxCitizenships = 4

const citizenshipListFromValue = (value?: string | null) => {
  const normalized = normalizeCountryValue(value)
  if (!normalized) return ['']
  const countries = normalized.split(',')
  return countries.length > 0 ? countries : ['']
}

const citizenshipValueFromList = (countries: string[]) => {
  const trimmed = [...countries]
  while (trimmed.length > 1 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop()
  }
  return trimmed.join(',')
}

const ageOption = generateAgeOptions()
const yearsAtAddressOptions = generateYearsOptions(true, 49)
const monthsAtAddressOptions = generateMonthOptions(true)

function AboutYou() {
  const navigate = usePersistedNavigation()
  const { state, dispatch } = useFormContext()
  const [citizenships, setCitizenships] = useState(() => citizenshipListFromValue(state.business.aboutYou.countryOfCitizenship))
  const { register, watch, clearErrors, formState: { errors, isSubmitted }, setValue, handleSubmit, trigger, unregister } = useForm({
    defaultValues: {
      firstName: state.business.aboutYou.firstName,
      middleName: state.business.aboutYou.middleName,
      lastName: state.business.aboutYou.lastName,
      phone: state.business.aboutYou.phone,
      email: state.business.aboutYou.email,
      dob: state.business.aboutYou.dob,
      maritalStatus: state.business.aboutYou.maritalStatus,
      isDependant: state.business.aboutYou.isDependant,
      dependantsNumber: state.business.aboutYou.dependantsNumber,
      dependantsAge: state.business.aboutYou.dependantsAge,
      address: state.business.aboutYou.address,
      city: state.business.aboutYou.city,
      ZIP: state.business.aboutYou.ZIP,
      country: normalizeCountryValue(state.business.aboutYou.country),
      reuseForPostalAddress: state.business.aboutYou.reuseForPostalAddress,
      timeAtAddressYears: state.business.aboutYou.timeAtAddressYears,
      timeAtAddressMonths: state.business.aboutYou.timeAtAddressMonths,
      previousAddress: state.business.aboutYou.previousAddress,
      previousCity: state.business.aboutYou.previousCity,
      previousZIP: state.business.aboutYou.previousZIP,
      previousCountry: normalizeCountryValue(state.business.aboutYou.previousCountry),
      previousTimeAtAddressYears: state.business.aboutYou.previousTimeAtAddressYears,
      previousTimeAtAddressMonths: state.business.aboutYou.previousTimeAtAddressMonths,
      postAddress: state.business.aboutYou.postAddress,
      postCity: state.business.aboutYou.postCity,
      postZIP: state.business.aboutYou.postZIP,
      postCountry: normalizeCountryValue(state.business.aboutYou.postCountry),
      nzResident: state.business.aboutYou.nzResident,
      countryOfBirth: normalizeCountryValue(state.business.aboutYou.countryOfBirth),
      countryOfCitizenship: normalizeCountryValue(state.business.aboutYou.countryOfCitizenship),
      countryOfAnotherCitizen: state.business.aboutYou.countryOfAnotherCitizen,
      driverLicenceType: state.business.aboutYou.driverLicenceType,
      driverLicenceNumber: state.business.aboutYou.driverLicenceNumber,
      driverLicenceVersion: state.business.aboutYou.driverLicenceVersion
    }
  })

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => handleSubmit(() => {})()

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  const isDependantYes = watch('isDependant') === true
  const dependantsAge = watch('dependantsAge', [])
  const showYourPostalAddress = !watch('reuseForPostalAddress')
  const showPreviousAddress = (watch('timeAtAddressYears') ?? 0) < 3
  const doesUserHaveDrivingLicence = watch('driverLicenceType') !== 'No licence'

  useEffect(() => {
    register('maritalStatus', { required: 'Marital status is required' })
    register('isDependant', { 
      validate: (value) => value === true || value === false || 'Is dependant is required'
    })
    register('address', { required: 'Address is required' })
    register('country', { required: 'Country is required' })
    register('timeAtAddressYears', { required: 'Years at address is required' })
    register('timeAtAddressMonths', { required: 'Months at address is required' })
    register('nzResident', { 
      validate: (value) => value === true || value === false || 'New Zealand resident is required'
    })
    register('countryOfBirth', { required: 'Country of birth is required' })
    register('countryOfCitizenship', { required: 'Country of citizenship is required' })
    register('driverLicenceType', { required: 'Driver licence type is required' })

    if (isDependantYes && Array.isArray(dependantsAge)) {
      dependantsAge.forEach((_, index) => {
        register(`dependantsAge.${index}`, { required: 'Age of dependant is required' })
      })
    } else if (Array.isArray(dependantsAge)) {
      dependantsAge.forEach((_, index) => {
        unregister(`dependantsAge.${index}`)
      })
    }
  })

  useEffect(() => {
    if (showYourPostalAddress) {
      register('postAddress', { required: 'Postal address is required' })
      register('postCountry', { required: 'Postal country is required' })
    } else {
      unregister('postAddress')
      unregister('postCountry')
    }

    if (showPreviousAddress) {
      register('previousAddress', { required: 'Previous address is required' })
      register('previousCity', { required: 'Previous city is required' })
      register('previousCountry', { required: 'Previous country is required' })
      register('previousTimeAtAddressYears')
      register('previousTimeAtAddressMonths')
    } else {
      unregister('previousAddress')
      unregister('previousCity')
      unregister('previousCountry')
      unregister('previousTimeAtAddressYears')
      unregister('previousTimeAtAddressMonths')
      unregister('previousZIP')
    }
  }, [showYourPostalAddress, showPreviousAddress, register, unregister])

  const handleCustomInputChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    setValue(fieldName, value)

    if (['address', 'previousAddress', 'postAddress'].includes(fieldName)) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      trigger(fieldName)
    }

    if (isSubmitted) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      trigger(fieldName)

      if (fieldName === 'driverLicenceType') {
        if (value === 'No licence') {
          setValue('driverLicenceNumber', '')
          setValue('driverLicenceVersion', '')
          clearErrors('driverLicenceNumber')
          clearErrors('driverLicenceVersion')
        }
      }
    }
  }

  const addDependant = () => {
    setValue('dependantsAge', [...dependantsAge, ''])
  }

  const removeDependant = (index: number) => {
    setValue('dependantsAge', dependantsAge.filter((_, i: any) => i !== index))
  }

  const updateCitizenships = (countries: string[]) => {
    const next = countries.length > 0 ? countries : ['']
    setCitizenships(next)
    handleCustomInputChange('countryOfCitizenship', citizenshipValueFromList(next))
  }

  const handleCitzenshipChange = (_fieldName: 'countryOfCitizenship', value: string, index: number) => {
    const next = citizenships.slice()
    next[index] = value
    updateCitizenships(next)
  }

  const handleCitizenshipAdd = () => {
    if (citizenships.length >= maxCitizenships) return
    updateCitizenships([...citizenships, ''])
  }

  const handleCitizenshipRemove = (index: number) => {
    if (index < 1) return
    updateCitizenships(citizenships.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleCustomAddressFinderInputChange = (fieldName: string, value: string, address: Address) => {
    if (address) {
      if (fieldName === 'previousAddress') {
        updateAddressFields('previous', address)
      } else if (fieldName === 'postAddress') {
        updateAddressFields('post', address)
      } else {
        updateAddressFields('', address)
      }
    }

    handleCustomInputChange(fieldName, value)
  }

  console.log('errors', errors)

  const updateAddressFields = (prefix: string, address: Address) => {
    const cityField = prefix ? `${prefix}City` : 'city'
    const zipField = prefix ? `${prefix}ZIP` : 'ZIP'
    const countryField = prefix ? `${prefix}Country` : 'country'

    if (address.city) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      setValue(cityField, address.city); clearErrors(cityField)
    }
    if (address.postcode) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      setValue(zipField, address.postcode); clearErrors(zipField)
    }
    if (address.country) {
      const countryISO3 = Object.keys(ISO_3166_MAPPER).find(key => ISO_3166_MAPPER[key] === address.country)

      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      setValue(countryField, countryISO3); clearErrors(countryField)

      let payload = {}
      payload = {
        ...state.business.aboutYou,
        [countryField]: countryISO3
      }

      dispatch({
        type: 'UPDATE_BUSINESS',
        step: 'aboutYou',
        payload
      })
    }
  }

  const { handleFormSave } = useFormSubmission({
    onSuccess: (response) => {
      console.log('Form submission successful:', response)
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_BUSINESS',
      step: 'aboutYou',
      payload: data.business.aboutYou
    })
  }

  const onAboutYouSubmit = (data: any) => {
    data = {
      ...state,
      personal: null,
      business: {
        ...state.business,
        aboutYou: {
          ...state.business.aboutYou,
          ...data,
          dependantsNumber: isDependantYes ? dependantsAge.length : 0,
          countryOfCitizenship: data.countryOfCitizenship
        }
      }
    }

    dispatchFormData(data)

    // See AboutYourBusiness.tsx for the explanation of this
    const payload = preparePayload(data, 'business', 'aboutYou')

    if (state.business.aboutYourBusiness.businessType === 'SOLE') {
      payload.business.aboutYourBusiness.limitedCompany = null
    } else if (state.business.aboutYourBusiness.businessType === 'LIMITED') {
      payload.business.aboutYourBusiness.soleTrader = null
    }

    handleFormSave(payload, ROUTES.BUSINESS.FINANCIAL_POSITION.path)
  }

  return (
    <>
      <Header />
      <form onSubmit={async (e) => {
        handleSubmit(onAboutYouSubmit)(e)
        
        window.setTimeout(async () => {
          if (Object.keys(errors).length > 0) {
            scrollToFirstError()
          }
        }, 0)
      }}>
        <Heading>Personal information</Heading>
        <Box>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <Input
              label='First name'
              inputId='firstName'
              required={true}
              defaultValue={state.business.aboutYou.firstName}
              placeholder='First Name'
              {...register('firstName', { required: 'First name is required' })}
              error={errors.firstName}
            />
            <Input
              label='Middle name'
              inputId='middleName'
              placeholder='Middle Name'
              defaultValue={state.business.aboutYou.middleName}
              {...register('middleName')}
              error={errors.middleName}
            />
            <Input
              label='Last name'
              inputId='lastName'
              required={true}
              defaultValue={state.business.aboutYou.lastName}
              placeholder='Last Name'
              {...register('lastName', { required: 'Last name is required' })}
              error={errors.lastName}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
            <Input
              label='Phone number'
              inputId='phoneNumber'
              required={true}
              defaultValue={state.business.aboutYou.phone}
              placeholder='Phone Number'
              {...register('phone', {
                required: 'Phone number is required',
                minLength: { value: 8, message: 'Mobile number must be at least 8 digits' },
                pattern: {
                  value: mobileNumberRegex,
                  message: 'Invalid mobile number format'
                }
              })}
              type='tel'
              error={errors.phone}
            />
            <Input
              label='Email address'
              inputId='EmailAdress'
              required={true}
              defaultValue={state.business.aboutYou.email}
              placeholder='Email Address'
              {...register('email', {
                required: 'Email address is required',
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email address format'
                }
              })}
              type='email'
              error={errors.email}
            />

          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            {/* Please confirm your date of birth. You need to be at least 18 to apply for a UDC Finance business loan. */}
            <Input
              label='Date of birth'
              inputId='DateofBirth'
              required={true}
              defaultValue={state.business.aboutYou.dob}
              {...register('dob', {
                required: 'Date of birth is required',
                validate: (value) => {
                  const date = new Date(value)
                  const today = new Date()
                  const age = today.getFullYear() - date.getFullYear()
                  if (age < 18) {
                    return 'Please confirm your date of birth. You need to be at least 18 to apply for a UDC Finance business loan.'
                  }
                  return true
                }
              })}
              type='date'
              min={minDateOfBirth}
              max={maxDateOfBirth}
              error={errors.dob}
            />
            <Select
              label='Marital status'
              required={true}
              selectOptions={martialStatusOptions}
              defaultValue={state.business.aboutYou.maritalStatus}
              error={errors.maritalStatus}
              onChange={value => handleCustomInputChange('maritalStatus', value)}
            />
            <Question
              title='Do you have a dependant? *'
              name='isDependantYes'
              style='field'
              defaultValue={state.business.aboutYou.isDependant}
              options={[
                { id: 'dependantYes', title: 'Yes', value: true },
                { id: 'dependantNo', title: 'No', value: false }
              ]}
              onChange={value => handleCustomInputChange('isDependant', value)}
              error={errors.isDependant}
            />
          </div>
          {isDependantYes &&
            <>
              <hr className='text-[var(--form-box-divider)] md:mt-16' />
              {dependantsAge.map((_, index) => (
                <div key={index} className='flex md:grid md:grid-cols-3 gap-2 md:gap-8'>
                  <div className='grow'>
                    <Select
                      defaultValue={dependantsAge[index]}
                      label='Age of dependant'
                      required={true}
                      selectOptions={ageOption}
                      onChange={(item) => setValue(`dependantsAge.${index}`, item)}
                      error={errors.dependantsAge?.[index]}
                    />
                  </div>
                  {index > 0 &&
                    <div>
                      <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)]'>&nbsp;</span>
                      <div className='relative mt-2.5'>
                        <Button icon='remove' style='text' onClick={() => removeDependant(index)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  }
                </div>
              ))}
              <div>
                <Button icon='add' style='text' onClick={addDependant}>
                  Add another dependant
                </Button>
              </div>
            </>
          }

        </Box>
        <Heading as='h2'>Personal address details</Heading>
        <Box>
          <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
            <div className="colspan-1">
              <AddressFinderInput
                label='Address'
                placeholder='Address'
                inputId='address'
                required={true}
                defaultValue={state.business.aboutYou.address}
                onChange={(value, address) => handleCustomAddressFinderInputChange('address', value, address)}
                error={errors.address}
              />
            </div>
            <Input
              label='City'
              placeholder='City'
              inputId='city'
              required={true}
              {...register('city', { required: 'City is required' })}
              error={errors.city}
            />
            <Input
              label='Postal code'
              placeholder='Postal code'
              inputId='ZIP'
              required={true}
              {...register('ZIP', { required: 'Postal code is required' })}
              error={errors.ZIP}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
            <Select
              label='Country'
              required={true}
              selectOptions={listOfCountries}
              defaultValue={normalizeCountryValue(state.business.aboutYou.country) || ''}
              onChange={value => handleCustomInputChange('country', value)}
              error={errors.country}
            />
            <fieldset>
              <legend className='block pl-2.5 text-sm/6 text-[var(--theme-text)] font-[var(--font-weight-bold)]'>Same as postal address</legend>
              <Checkbox
                label='Postal address'
                inputId='reuseForPostalAddress'
                defaultChecked={state.business.aboutYou.reuseForPostalAddress}
                {...register('reuseForPostalAddress')}
              />
            </fieldset>
          </div>
          <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
            <Select
              label='Time at current address'
              required={true}
              selectOptions={yearsAtAddressOptions}
              defaultValue={state.business.aboutYou.timeAtAddressYears !== null ? state.business.aboutYou.timeAtAddressYears : undefined}
              onChange={value => handleCustomInputChange('timeAtAddressYears', value)}
              error={errors.timeAtAddressYears}
            />
            <Select
              label='Months'
              hideLabel={true}
              required={true}
              selectOptions={monthsAtAddressOptions}
              defaultValue={state.business.aboutYou.timeAtAddressMonths !== null ? state.business.aboutYou.timeAtAddressMonths : undefined}
              onChange={value => handleCustomInputChange('timeAtAddressMonths', value)}
              error={errors.timeAtAddressMonths}
            />
          </div>
        </Box>

        { showPreviousAddress && (
          <>
            <Heading as='h2'>Previous address details</Heading>
            <Box>
              <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                <div className="colspan-1">
                  <AddressFinderInput
                    label='Address'
                    placeholder='Address'
                    inputId='previousAddress'
                    required={true}
                    defaultValue={state.business.aboutYou.previousAddress}
                    onChange={(value, address) => handleCustomAddressFinderInputChange('previousAddress', value, address)}
                    error={errors.previousAddress}
                  />
                </div>
                <Input
                  label='City'
                  placeholder='City'
                  inputId='previousCity'
                  required={true}
                  {...register('previousCity', { required: 'City is required' })}
                  error={errors.previousCity}
                />
                <Input
                  label='Postal code'
                  placeholder='Postal code'
                  inputId='previousPostalCode'
                  required={true}
                  {...register('previousZIP', { required: 'Postal code is required' })}
                  error={errors.previousZIP}
                />
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Select
                  label='Country'
                  required={true}
                  selectOptions={listOfCountries}
                  defaultValue={normalizeCountryValue(state.business.aboutYou.previousCountry) || ''}
                  onChange={value => handleCustomInputChange('previousCountry', value)}
                  error={errors.previousCountry}
                />
              </div>
              <div className='grid grid-cols-2 md:grid-cols-3 gap-7 md:gap-8'>
                <Select
                  label='Time at previous address'
                  selectOptions={yearsAtAddressOptions}
                  defaultValue={state?.business?.aboutYou?.previousTimeAtAddressYears || ''}
                  onChange={value => handleCustomInputChange('previousTimeAtAddressYears', value)}
                  error={errors.previousTimeAtAddressYears}
                />
                <Select
                  label='Months'
                  hideLabel={true}
                  selectOptions={monthsAtAddressOptions}
                  defaultValue={state?.business?.aboutYou?.previousTimeAtAddressMonths || ''}
                  onChange={value => handleCustomInputChange('previousTimeAtAddressMonths', value)}
                  error={errors.previousTimeAtAddressMonths}
                />
              </div>
            </Box>
          </>
        )}

        { showYourPostalAddress &&
          <>
            <Heading as='h2'>Postal address details</Heading>
            <Box>
              <div className='grid grid-cols-1 md:grid-cols-3  gap-7 md:gap-8'>
                <div className="colspan-1">
                  <AddressFinderInput
                    label='Address'
                    placeholder='Address'
                    inputId='postalAddress'
                    required={true}
                    defaultValue={state.business.aboutYou.postAddress}
                    onChange={(value, address) => handleCustomAddressFinderInputChange('postAddress', value, address)}
                    error={errors.postAddress}
                  />
                </div>
                <Input
                  label='City'
                  placeholder='City'
                  inputId='postalCity'
                  required={true}
                  {...register('postCity', { required: 'City is required' })}
                  error={errors.postCity}
                />
                <Input
                  label='Postal code'
                  placeholder='Postal code'
                  inputId='postalPostalCode'
                  required={true}
                  {...register('postZIP', { required: 'Postal code is required' })}
                  error={errors.postZIP}
                />
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Select
                  label='Country'
                  required={true}
                  selectOptions={listOfCountries}
                  defaultValue={normalizeCountryValue(state.business.aboutYou.postCountry) || ''}
                  onChange={value => handleCustomInputChange('postCountry', value)}
                  error={errors.postCountry}
                />
              </div>
            </Box>
          </>
        }
        <Box>
          <Question
            title='Are you a New Zealand citizen or resident? *'
            name='nzResident'
            defaultValue={state.business.aboutYou.nzResident}
            onChange={value => handleCustomInputChange('nzResident', value)}
            error={errors.nzResident}
            options={[
              { id: 'nzResidentYes', title: 'Yes', value: true },
              { id: 'nzResidentNo', title: 'No', value: false }
            ]}
          />
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <Select
              label='Country of birth'
              required={true}
              selectOptions={listOfCountries}
              defaultValue={normalizeCountryValue(state.business.aboutYou.countryOfBirth) || ''}
              onChange={value => handleCustomInputChange('countryOfBirth', value)}
              error={errors.countryOfBirth}
            />
            <Select
              label='Country of citizenship'
              required={true}
              selectOptions={listOfCountries}
              defaultValue={citizenships[0] || ''}
              onChange={value => handleCitzenshipChange('countryOfCitizenship', value, 0)}
              error={errors.countryOfCitizenship}
            />
            <div>
              <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
              <div className='relative mt-2.5'>
                {citizenships.length < maxCitizenships && (
                  <Button icon='add' style='text' onClick={handleCitizenshipAdd}>
                    Add new citizenship
                  </Button>
                )}
              </div>
            </div>
          </div>
          {citizenships.slice(1).map((country, index) => (
            <div key={`${country || 'empty'}-${index}`} className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <div className='max-md:hidden' />
              <Select
                label='Country of citizenship'
                required={true}
                selectOptions={listOfCountries}
                defaultValue={country}
                onChange={value => handleCitzenshipChange('countryOfCitizenship', value, index + 1)}
                error={errors.countryOfCitizenship}
              />
              <div>
                <span className='block h-6 pl-2.5 text-sm/6 text-[var(--theme-text)] uppercase font-[var(--font-weight-bold)] max-md:hidden'>&nbsp;</span>
                <div className='relative mt-2.5'>
                  <Button icon='remove' onClick={() => handleCitizenshipRemove(index + 1)} style='text'>
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <hr className='text-[var(--form-box-divider)] md:mt-16' />
          <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
            <Select
              label='Driver licence type'
              required={true}
              selectOptions={drivingLicenceOptions}
              defaultValue={state.business.aboutYou.driverLicenceType}
              onChange={value => handleCustomInputChange('driverLicenceType', value)}
              error={errors.driverLicenceType}
            />
            <Input
              label='Driver licence number'
              placeholder={doesUserHaveDrivingLicence ? 'Driver licence number' : 'No driver licence number needed'}
              inputId='driversLicenceNumber'
              required={doesUserHaveDrivingLicence}
              defaultValue={state.business.aboutYou.driverLicenceNumber}
              maxLength={8}
              {...register('driverLicenceNumber', {
                required: doesUserHaveDrivingLicence ? 'Driver licence number is required' : false
              })}
              disabled={!doesUserHaveDrivingLicence}
              error={errors.driverLicenceNumber}
            />
            <Input
              label='Driver licence version'
              placeholder={doesUserHaveDrivingLicence ? 'Driver licence version' : 'No driver licence version needed'}
              inputId='driversLicenceVersion'
              required={doesUserHaveDrivingLicence}
              defaultValue={state.business.aboutYou.driverLicenceVersion}
              maxLength={3}
              {...register('driverLicenceVersion', {
                required: doesUserHaveDrivingLicence ? 'Driver licence version is required' : false
              })}
              disabled={!doesUserHaveDrivingLicence}
              error={errors.driverLicenceVersion}
            />
          </div>
        </Box>

        {state.isPostCall && (
          <p className="text-center text-gray-600 mt-6 max-w-2xl mx-auto">By clicking "Next" you are confirming that the above information is correct</p>
        )}

        <div className='flex justify-between mt-8 pb-16 max-w-5xl mx-auto'>
          <Button iconPosition='left' onClick={() => navigate(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)}>Back</Button>
          <Button
            // onClick={() => navigate(ROUTES.BUSINESS.FINANCIAL_POSITION.path)} 
            type='submit'>Next</Button>
        </div>
      </form>
    </>
  )
}

export default AboutYou
